import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrders } from '../lib/api';
import { getCartSessionId } from '../lib/cartSession';
import { getOrderAddressLine } from '../lib/addressDisplay';
import { formatNpr } from '../lib/currency';

function OrdersPage() {
  const location = useLocation();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadOrders() {
      try {
        const nextOrders = await getOrders({
          sessionId: getCartSessionId(),
          userId: user?.id || '',
        });

        if (isMounted) {
          setOrders(nextOrders);
          setError('');
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadOrders();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  return (
    <section className="page-card">
      <h1 className="page-card__title">Orders</h1>
      <p className="page-card__text">See your latest orders, totals, and delivery details in one place.</p>
      {location.state?.placedOrder ? (
        <div className="orders-success">
          <strong>Order placed:</strong> {location.state.placedOrder.orderNumber}
        </div>
      ) : null}
      {location.state?.placedOrder && !user ? (
        <div className="orders-account-prompt">
          <div>
            <strong>Create account to track your order</strong>
            <p>Save your details for faster checkout next time.</p>
          </div>
          <div className="orders-account-prompt__actions">
            <Link
              className="green-button orders-account-prompt__primary"
              to="/signup"
              state={{ from: '/checkout' }}
            >
              Create Account
            </Link>
            <Link className="product-card__link" to="/login" state={{ from: '/checkout' }}>
              Login
            </Link>
          </div>
        </div>
      ) : null}
      {isLoading ? <p className="products-state">Loading orders...</p> : null}
      {error ? <p className="products-state products-state--error">{error}</p> : null}
      {!isLoading && !error && orders.length === 0 ? (
        <p className="products-state">No orders yet. Place your first cash on delivery order.</p>
      ) : null}
      <div className="orders-list">
        {orders.map((order) => (
          <article key={order.id} className="order-card">
            <div className="order-card__header">
              <div>
                <h2>{order.orderNumber}</h2>
                <p>{new Date(order.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
              </div>
              <div className="order-card__meta">
                <span className={`order-card__status order-card__status--${order.status.toLowerCase()}`}>
                  {order.status}
                </span>
                <span className="order-card__payment">{order.paymentMethod.replaceAll('_', ' ')}</span>
              </div>
            </div>
            <div className="order-card__address">
              📍 {getOrderAddressLine(order.shippingAddress)}
            </div>
            <div className="order-card__items">
              {order.items.map((item) => (
                <div key={item.id} className="order-card__item">
                  <div className="order-card__item-image">
                    {item.product.category === 'Eye Examinations' ? '👁️' : 
                     item.product.category === 'Eyewear' ? '👓' : 
                     item.product.category === 'Contact Lenses' ? '🔍' : '🛍️'}
                  </div>
                  <div className="order-card__item-info">
                    <div className="order-card__item-name">{item.product.name}</div>
                    <div className="order-card__item-quantity">Quantity: {item.quantity}</div>
                  </div>
                  <div className="order-card__item-price">{formatNpr(item.lineTotal)}</div>
                </div>
              ))}
            </div>
            <div className="order-card__footer">
              <span className="order-card__footer-items">{order.itemCount} items</span>
              <span className="order-card__footer-total">Total: {formatNpr(order.total)}</span>
            </div>
          </article>
        ))}
      </div>
      <p className="auth-form__switch">
        Need more products? <Link to="/products">Continue shopping</Link>
      </p>
    </section>
  );
}

export default OrdersPage;
